<template>
  <Html :data-n-head-ssr="isHydrating">
    <LazyAppMaintenance v-if="maintenanceMode" />
    <template v-else>
      <NuxtLoadingIndicator color="var(--color-secondary)" />
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>
    </template>

    <TToastQueue />
    <Modals />
    <LazyAppAuthModal v-if="!isLoggedIn || profileCompletionPending" />
  </Html>
</template>

<script setup lang="ts">
import '@stripe/stripe-js'
import { PHONE_NUMBER } from '~/constants'

import type { UseAuthentication } from '~/types/rental-api-aliases'

const { maintenanceMode } = useMaintenanceMode()
const runtimeConfig = useRuntimeConfig()
const profileCompletionPending = useProfileCompletionPending()

/**
 * It's used by the Automated tests to know when hydration is done.
 */
const { hydrationStatus } = useHydrationState()
const isHydrating = computed(() => {
  return hydrationStatus.value ? undefined : true
})

useSeoHeadTags({
  title: computed(() => 'RV Rentals'),
  description: computed(() => 'Best RV rentals in North America'),
})

const verifiedReviews = useVerifiedReviews()

useSchemaOrg([
  defineOrganization({
    name: 'RVezy',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        'areaServed': 'US',
        'contactType': 'customer service',
        'telephone': PHONE_NUMBER,
        'contactOption': 'https://schema.org/TollFree',
      },
      {
        '@type': 'ContactPoint',
        'areaServed': 'CA',
        'contactType': 'customer service',
        'telephone': PHONE_NUMBER,
        'contactOption': 'https://schema.org/TollFree',
        'availableLanguage': ['English', 'French'],
      },
    ],
    subOrganization: [
      defineLocalBusiness({
        name: 'RVezy USA Inc.',
        address: {
          streetAddress: '1209 Orange Street',
          addressLocality: 'Wilmington',
          addressRegion: 'DE',
          addressCountry: 'US',
          postalCode: '19801',
        },
      }),
      defineLocalBusiness({
        name: 'RVezy Inc.',
        address: {
          streetAddress: '245 Cooper Street',
          addressLocality: 'Ottawa',
          addressRegion: 'Ontario',
          addressCountry: 'CA',
          postalCode: 'K2P 0G2',
        },
      }),
    ],
    sameAs: [
      'https://www.facebook.com/rvezy',
      'https://www.youtube.com/c/RVezy',
      'https://www.instagram.com/myrvezy',
      'https://x.com/myrvezy',
      'https://ca.linkedin.com/company/rvezy.com',
    ],
    ...(verifiedReviews.value
      ? {
          aggregateRating: defineAggregateRating({
            ratingValue: verifiedReviews.value.rating,
            reviewCount: verifiedReviews.value.reviewCount,
            bestRating: 5,
          }),
        }
      : {}),
  }),
])

/**
 * Initialize the app.
 */
const { $experiment } = useNuxtApp()
const { status, isLoggedIn } = useAuthentication()
const { isHostMode } = useBaseName()
const { user, userExists, userId } = useUser()
const rtsessionIdCookie = useRtsessionIdCookie()

const { getAndSetPermissions, resetPermissions } = usePermissionsData()
const { getAndSetNotifications, resetNotifications, getAndSetInsights, resetInsights } = useNotificationsData()
const { getAndSetFavourites, resetFavourites } = useFavouritesData()

async function identifyUser() {
  if (isMetaClient && userExists.value) {
    // Analytics tracking
    trackUserAuthentication((user.value as UseAuthentication), rtsessionIdCookie.value ?? '')

    // Braze tracking
    let userID = userId.value.toString()
    if (runtimeConfig.public.braze.userPrefix) {
      userID = `${runtimeConfig.public.braze.userPrefix}_${userId.value}`
    }

    const { setUser } = await useBraze()
    setUser(userID)
  }
}

function unidentifyUser() {
  if (isMetaClient) {
    trackUserDeauthentication()
  }
}

// Initially fetch user-related data and watch for subsequent status changes.
await useAsyncData('user-data-and-statistics', async () => {
  if (status.value === 'authenticated') {
    const tasks = [
      getAndSetFavourites(),
      getAndSetPermissions(),
      getAndSetNotifications(),
    ]

    if (user.value?.HasRv) {
      tasks.push(getAndSetInsights())
    }

    await Promise.allSettled(tasks)
  }

  if (status.value === 'unauthenticated') {
    resetFavourites()
    resetPermissions()
    resetNotifications()
    resetInsights()
  }

  return { loggedIn: status.value === 'authenticated' }
}, {
  watch: [status],
})

// Watch the status and call client-specific logic that's not needed in SSR.
watch((status), async (newStatus, oldStatus) => {
  if (newStatus === 'authenticated') {
    await identifyUser()
  }

  if (oldStatus && newStatus === 'unauthenticated') {
    unidentifyUser()
  }

  if (newStatus !== 'loading') {
    await $experiment.evaluateExperiments()
  }
}, {
  immediate: true,
})

// When the host mode changes, refetch the notifications for the user.
watch(isHostMode, async () => {
  if (isLoggedIn.value) {
    await getAndSetNotifications()
  }
})

onMounted(async () => {
  const { openSession } = await useBraze()
  openSession()
})

// When a user's profile is incomplete, we prompt them to complete it and we
// redirect them if there was an intent.
const pendingRequestToBookUrl = usePendingRequestToBookUrlCookie()
const { userProfileState } = useUser()

watch(userProfileState, (profileState) => {
  if (!pendingRequestToBookUrl.value || profileState !== 'Incomplete') {
    return
  }

  useProfileCompletionPending().value = true
  useAuthenticationModal().value = true
}, {
  immediate: true,
})
</script>
